/// <amd-module name="Repos/VersionDialogs/VersionDialogActionsService" />
import * as React from "react";
import { getGitRepository } from "Repos/Common//VCViewModelDataProviderService";
import { GitAnnotatedTag, GitRefUpdate, GitRefUpdateResult } from "Repos/Common/Contracts/Git";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { CreateBranchDialog } from "./Components/Create/CreateBranchDialog";
import { ICreateBranchDialogProps } from "./Components/Create/CreateBranchDialog.Props";
import { CreateTagDialog } from "./Components/Create/CreateTagDialog";
import { ICreateTagDialogProps } from "./Components/Create/CreateTagDialog.Props";
import { DeleteBranchDialog } from "./Components/Delete/DeleteBranchDialog";
import { IDeleteBranchDialogProps } from "./Components/Delete/DeleteBranchDialog.Props";
import { DeleteTagDialog } from "./Components/Delete/DeleteTagDialog";
import { IDeleteTagDialogProps } from "./Components/Delete/DeleteTagDialog.Props";

/**
 * Service for performing version dialog actions like opening create branch/create tag.
 */
interface IVersionDialogActionsService extends IVssService {
    createBranch: (props: ICreateBranchDialogProps) => void;
    createTag: (props: ICreateTagDialogProps) => void;
    deleteBranch: (props: IDeleteBranchDialogProps) => void;
    deleteTag: (props: IDeleteTagDialogProps) => void;
}

class VersionDialogActionsService extends VssService implements IVersionDialogActionsService {
    public createBranch(props: ICreateBranchDialogProps): void {
        if (props && !props.repository) {
            props.repository = getGitRepository(this.pageContext);
        }
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const dialogProps = {
                ...props,
                onComplete: (createdBranch: GitRefUpdate) => {
                    onDismiss();
                    props.onDismiss?.();
                    props.onComplete && props.onComplete(createdBranch);
                },
                onDismiss: () => {
                    onDismiss();
                    props.onDismiss?.();
                }
            };

            return <CreateBranchDialog {...dialogProps} />;
        });
    }

    public createTag(props: ICreateTagDialogProps): void {
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const dialogProps = {
                ...props,
                onComplete: (createdTag: GitAnnotatedTag) => {
                    onDismiss();
                    props.onDismiss?.();
                    props.onComplete && props.onComplete(createdTag);
                },
                onDismiss: () => {
                    onDismiss();
                    props.onDismiss?.();
                }
            };

            return <CreateTagDialog {...dialogProps} />;
        });
    }

    public deleteBranch(props: IDeleteBranchDialogProps): void {
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const dialogProps = {
                ...props,
                onComplete: (deletedBranch: GitRefUpdateResult) => {
                    onDismiss();
                    props.onDismiss?.();
                    props.onComplete && props.onComplete(deletedBranch);
                },
                onDismiss: () => {
                    onDismiss();
                    props.onDismiss?.();
                }
            };

            return <DeleteBranchDialog {...dialogProps} />;
        });
    }

    public deleteTag(props: IDeleteTagDialogProps): void {
        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const dialogProps = {
                ...props,
                onComplete: (deletedTag: GitRefUpdateResult) => {
                    onDismiss();
                    props.onDismiss?.();
                    props.onComplete && props.onComplete(deletedTag);
                },
                onDismiss: () => {
                    onDismiss();
                    props.onDismiss?.();
                }
            };

            return <DeleteTagDialog {...dialogProps} />;
        });
    }
}

const serviceName = "IVersionDialogActionsService";
Services.add(serviceName, { serviceFactory: VersionDialogActionsService });

function getService(pageContext: IVssPageContext): IVersionDialogActionsService {
    return pageContext.getService<IVersionDialogActionsService>(serviceName);
}

/**
 * Opens create branch dialog using the specified props. If no initial reference version
 * specified, default branch of the specified repository will be selected.
 *
 * @param pageContext Page context.
 * @param props Create branch dialog props.
 */
export function createBranch(pageContext: IVssPageContext, props: ICreateBranchDialogProps): void {
    getService(pageContext).createBranch(props);
}

/**
 * Opens create tag dialog using the specified props. If no initial reference version
 * specified, default branch of the specified repository will be selected.
 *
 * @param pageContext Page context.
 * @param props Create tag dialog props.
 */
export function createTag(pageContext: IVssPageContext, props: ICreateTagDialogProps): void {
    getService(pageContext).createTag(props);
}

/**
 * Opens delete branch dialog tag using the specified props.
 *
 * @param pageContext Page context.
 * @param props Delete branch dialog props.
 */
export function deleteBranch(pageContext: IVssPageContext, props: IDeleteBranchDialogProps): void {
    getService(pageContext).deleteBranch(props);
}

/**
 * Opens delete tag dialog tag using the specified props.
 *
 * @param pageContext Page context.
 * @param props Delete tag dialog props.
 */
export function deleteTag(pageContext: IVssPageContext, props: IDeleteTagDialogProps): void {
    getService(pageContext).deleteTag(props);
}
